import {Tooltip, Typography, TableSortLabel} from '@material-ui/core';
import React, {useContext} from 'react';
import getFilterComponent, {FilterProperties} from 'Shared/components/PagedView/Filter';
import 'Shared/components/PagedView/Table/ColumnHeader.sass';
import TableFilterMenu from 'Shared/components/PagedView/Table/TableFilterMenu';
import Draggable from 'react-draggable';
import {ColumnWidthsContext} from 'Shared/components/PagedView/Table/Table';
import {DragIndicator} from '@material-ui/icons';
import {Sort} from 'Shared/components/PagedView/Table/Sort';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';

export type SharedColumnHeaderProps<TFilter, TData> = {
  title: string;
  sortColumnKey?: keyof TData;
  filterProperties?: FilterProperties<TFilter>;
};

export type ColumnHeaderProps<TFilter, TData> = {
  onColumnResize: (columnKey: string, newSize: number) => void;
  onChangeSort: ((sort?: Sort) => void) | undefined;
  sort: Sort | undefined;
  isLastColumn: boolean;
} & SharedColumnHeaderProps<TFilter, TData>;

const getNextSortState = (sort?: Sort) => (columnName: string): Sort | undefined =>
  !sort
    ? {direction: 'asc', column: columnName}
    : sort.direction === 'asc'
    ? {direction: 'desc', column: columnName}
    : undefined;

const ColumnHeader = <TFilter, TData>(props: ColumnHeaderProps<TFilter, TData>) => {
  const {onColumnResize, sort, title, sortColumnKey, isLastColumn, filterProperties, onChangeSort} = props;
  const columnWidths = useContext(ColumnWidthsContext);
  const FilterComponent = filterProperties ? getFilterComponent(title, filterProperties) : null;

  return (
    <div
      id={`column-header-${title}`}
      key={`column-header__${title}`}
      className="paged-table__cell"
      style={{width: `${columnWidths.get(title)}%`}}
    >
      <div className="column-header__table-cell-content">
        <div className="column-header__titles">
          <Tooltip title={title}>
            {onChangeSort && sortColumnKey ? (
              <TableSortLabel
                className="column-header__sort-label"
                active={!!sort && sort.column === sortColumnKey}
                direction={sort && sort.direction}
                onClick={() => {
                  const columnAlreadyHasSort = sort?.column === sortColumnKey;

                  if (columnAlreadyHasSort) {
                    onChangeSort(getNextSortState(sort)(sortColumnKey as string));
                  } else {
                    onChangeSort({direction: 'asc', column: sortColumnKey as string});
                  }
                }}
              >
                <Typography id={`column-header-${toKebabCase(title)}`} className="column-header__label">{title}</Typography>
              </TableSortLabel>
            ) : (
              <Typography id={`column-header-${toKebabCase(title)}`} className="column-header__label">{title}</Typography>
            )}
          </Tooltip>

          {FilterComponent && filterProperties && (
            <TableFilterMenu
              title={title}
              filterProperties={filterProperties! as any}
              FilterComponent={FilterComponent}
            />
          )}
        </div>
        {!isLastColumn && (
          <div>
            <Draggable
              axis="x"
              defaultClassName="paged-table__change-column-size-icon"
              defaultClassNameDragging="paged-table__change-column-size-icon--active"
              onDrag={(event, {deltaX}) => {
                onColumnResize(title, deltaX);
              }}
              position={{x: 0, y: 0}}
            >
              <span>
                <div id={`column-header-resize-${toKebabCase(title)}`} className="column-header__resize-indicator">
                  <DragIndicator />
                </div>
              </span>
            </Draggable>
          </div>
        )}
      </div>
    </div>
  );
};

export default ColumnHeader;
